import {
  AFFILIATE_COOKIE_KEY,
  AFFILIATE_COOKIE_NAME,
  ATTRIBUTION_COOKIE_KEYS,
  ATTRIBUTION_COOKIE_NAME,
  ATTRIBUTION_COOKIE_PAID_NAME,
  ATTRIBUTION_COOKIE_PAID_KEYS,
} from '@/constants/cookies';
import { getCookie, setCookie } from './cookies';
import { createLogger } from './debug';

const FALLBACK_VALUE = '(not set)';

const logger = createLogger('attribution');

interface AttributionUTMs {
  [ATTRIBUTION_COOKIE_KEYS.utmCampaign]: string;
  [ATTRIBUTION_COOKIE_KEYS.utmContent]: string;
  [ATTRIBUTION_COOKIE_KEYS.utmMedium]: string;
  [ATTRIBUTION_COOKIE_KEYS.utmSource]: string;
  [ATTRIBUTION_COOKIE_KEYS.utmTerm]: string;
}

interface AttributionMisc {
  [ATTRIBUTION_COOKIE_KEYS.referrer]: string;
  [ATTRIBUTION_COOKIE_PAID_KEYS.gClid]: string;
  [ATTRIBUTION_COOKIE_PAID_KEYS.bingClkid]: string;
  [ATTRIBUTION_COOKIE_PAID_KEYS.hsAmt]: string;
}

type AttributionCookie = AttributionUTMs & AttributionMisc;

const parseQueryParams = () => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const hasUtm = Array.from(urlParams.keys()).some((key) => key.startsWith('utm_'));
  const hasAffiliate = urlParams.has(AFFILIATE_COOKIE_KEY);
  const hasGclid = urlParams.has(ATTRIBUTION_COOKIE_PAID_KEYS.gClid);
  const hasBingClkid = urlParams.has(ATTRIBUTION_COOKIE_PAID_KEYS.bingClkid);
  const hasHsa_mt = urlParams.has(ATTRIBUTION_COOKIE_PAID_KEYS.hsAmt);

  if (document.referrer) {
    logger.log(`Referrer present: ${document.referrer}`);
    updateReferrer();
  }

  if (hasUtm) {
    logger.log('UTMs present in the URL');
    setUtmsFromQuery();
  }

  if (hasAffiliate) {
    logger.log('Affiliate present in the URL');
    setAffiliateFromQuery();
  }

  if (hasGclid) {
    logger.log('GCLID present in the URL');
    setGclidFromQuery();
  }

  if (hasBingClkid) {
    logger.log('msclkid present in the URL');
    setMsclkidFromQuery();
  }

  if (hasHsa_mt) {
    logger.log('hsa_mt present in the URL');
    setHsaMtFromQuery();
  }
};

const getAttributionFromCookie = (): AttributionCookie => {
  const cookieData = getCookie(ATTRIBUTION_COOKIE_NAME, true);
  const cookieDataPaid = getCookie(ATTRIBUTION_COOKIE_PAID_NAME, true);

  return {
    [ATTRIBUTION_COOKIE_KEYS.utmCampaign]: '',
    [ATTRIBUTION_COOKIE_KEYS.utmContent]: '',
    [ATTRIBUTION_COOKIE_KEYS.utmMedium]: '',
    [ATTRIBUTION_COOKIE_KEYS.utmSource]: '',
    [ATTRIBUTION_COOKIE_KEYS.utmTerm]: '',
    [ATTRIBUTION_COOKIE_KEYS.referrer]: '',
    [ATTRIBUTION_COOKIE_PAID_KEYS.gClid]: '',
    [ATTRIBUTION_COOKIE_PAID_KEYS.bingClkid]: '',
    [ATTRIBUTION_COOKIE_PAID_KEYS.hsAmt]: '',
    ...(typeof cookieData === 'object' ? cookieData : {}),
    ...(typeof cookieDataPaid === 'object' ? cookieDataPaid : {}),
  };
};

const getAffiliateFromCookie = (): string => getCookie(AFFILIATE_COOKIE_NAME) ?? '';

const setUtmsFromQuery = (): void => {
  const urlParams = new URLSearchParams(window?.location?.search);

  const filteredUtms = Array.from(urlParams.entries()).filter(([key, value]) => {
    return (
      key.toLowerCase().startsWith('utm_') &&
      (Object.values(ATTRIBUTION_COOKIE_KEYS) as string[]).includes(key.toLowerCase())
    );
  });

  const newUtms: AttributionUTMs = {
    [ATTRIBUTION_COOKIE_KEYS.utmCampaign]:
      filteredUtms.find(([key]) => key === ATTRIBUTION_COOKIE_KEYS.utmCampaign)?.[1] ||
      FALLBACK_VALUE,
    [ATTRIBUTION_COOKIE_KEYS.utmContent]:
      filteredUtms.find(([key]) => key === ATTRIBUTION_COOKIE_KEYS.utmContent)?.[1] ||
      FALLBACK_VALUE,
    [ATTRIBUTION_COOKIE_KEYS.utmMedium]:
      filteredUtms.find(([key]) => key === ATTRIBUTION_COOKIE_KEYS.utmMedium)?.[1] ||
      FALLBACK_VALUE,
    [ATTRIBUTION_COOKIE_KEYS.utmSource]:
      filteredUtms.find(([key]) => key === ATTRIBUTION_COOKIE_KEYS.utmSource)?.[1] ||
      FALLBACK_VALUE,
    [ATTRIBUTION_COOKIE_KEYS.utmTerm]:
      filteredUtms.find(([key]) => key === ATTRIBUTION_COOKIE_KEYS.utmTerm)?.[1] ||
      FALLBACK_VALUE,
  };

  const cookieData = getAttributionFromCookie();

  logger.log('Updating cookie with new UTM data', newUtms);

  /*
   * setCookie(
   * ATTRIBUTION_COOKIE_NAME,
   * {
   * ...cookieData,
   * ...newUtms,
   * },
   * { expires: 30 },
   * );
   */
};

const setMsclkidFromQuery = (): void => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const msclkid = urlParams.get(ATTRIBUTION_COOKIE_PAID_KEYS.bingClkid) || FALLBACK_VALUE;
  const cookieData = getAttributionFromCookie();

  if (!msclkid) return;
  logger.log(`Updating cookie with new msclkid value: ${msclkid}`);

  // A setCookie call for ATTRIBUTION_COOKIE_PAID_NAME would go here
};

const setHsaMtFromQuery = (): void => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const hsa_mt = urlParams.get(ATTRIBUTION_COOKIE_PAID_KEYS.hsAmt) || FALLBACK_VALUE;
  const cookieData = getAttributionFromCookie();

  if (!hsa_mt) return;
  logger.log(`Updating cookie with new hsa_mt value: ${hsa_mt}`);

  // A setCookie call for ATTRIBUTION_COOKIE_PAID_NAME would go here
};

const setGclidFromQuery = (): void => {
  const urlParams = new URLSearchParams(window?.location?.search);

  const gclid = urlParams.get(ATTRIBUTION_COOKIE_PAID_KEYS.gClid) || FALLBACK_VALUE;
  const cookieData = getAttributionFromCookie();

  if (!gclid) return;

  logger.log(`Updating cookie with new gclid value: ${gclid}`);
  /*
   * setCookie(
   * ATTRIBUTION_COOKIE_PAID_NAME, // Note: This should likely be the PAID name
   * {
   * ...cookieData,
   * [ATTRIBUTION_COOKIE_PAID_KEYS.gClid]: gclid,
   * },
   * { expires: 30 },
   * );
   */
  if (gclid !== FALLBACK_VALUE) {
    /* Disabled until CORS issues are resolved */
    // front.sendEvent(FRONT_EVENTS.GCLID, gclid);
  }
};

const setAffiliateFromQuery = () => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const affiliate = urlParams.get(AFFILIATE_COOKIE_KEY);

  if (!affiliate) return;

  logger.log(`Updating affiliate cookie with new value: ${affiliate}`);

  setCookie(AFFILIATE_COOKIE_NAME, affiliate);
};

const updateReferrer = () => {
  const parsedReferrer = new URL(document.referrer);

  if (/^(?:.+\.)?front(?:app)?\.com$/i.test(parsedReferrer.hostname)) {
    logger.log(`Skipping referrer update: internal domain`);

    return;
  }

  const cookieData = getAttributionFromCookie();

  if (cookieData.referrer === document.referrer) {
    logger.log(`Skipping referrer update: same as cookied value`);

    return;
  }

  logger.log(`Updating cookie with new referrer value: ${document.referrer}`);

  /*
   * setCookie(
   * ATTRIBUTION_COOKIE_NAME,
   * {
   * ...cookieData,
   * [ATTRIBUTION_COOKIE_KEYS.referrer]: document.referrer,
   * },
   * { expires: 30 },
   * );
   */
};

const attribution = {
  parseQueryParams,
  getAttributionFromCookie,
  setUtmsFromQuery,
  setGclidFromQuery,
  setMsclkidFromQuery,
  setHsaMtFromQuery,
  getAffiliateFromCookie,
  setAffiliateFromQuery,
};

export default attribution;